/**
 * Screen 24 — Friends.
 *
 * Three tabs: Friends, Requests (incoming + outgoing pending), Challenges
 * (incoming + outgoing duel invites). A search bar at the top finds users
 * by username/email-prefix and lets the caller add them with one tap.
 *
 * Data:
 *   - GET /friends — accepted list with presence
 *   - GET /friends/requests + /friends/requests/outgoing — pending
 *   - GET /friends/suggestions — "people you may know" cards
 *   - GET /challenges/incoming + /challenges/outgoing — duel invites
 *
 * The challenge-from-friend flow opens a small bottom sheet so the duel
 * params (category/difficulty/stake) can be picked without leaving the
 * friends screen.
 */

import { api, ApiError } from '../api.js';
import { auth } from '../auth.js';
import { escape } from '../components/form.js';
import { toast } from '../components/toast.js';
import { navigate } from '../router.js';
import { formatKobo } from '../wallet.js';
import { refreshFriendsBadge } from '../lib/friends_badge.js';
import { ensurePresenceHeartbeat } from '../lib/presence.js';

const STAKES = [
  { kobo: 0, label: 'Free' },
  { kobo: 10_000, label: '₦100' },
  { kobo: 20_000, label: '₦200' },
  { kobo: 50_000, label: '₦500' },
];

let currentTab = 'friends';

function readTabFromUrl() {
  const params = new URLSearchParams(window.location.search);
  const t = params.get('tab');
  if (t === 'requests' || t === 'challenges' || t === 'friends') return t;
  return 'friends';
}

export async function renderFriends(root) {
  ensurePresenceHeartbeat();
  currentTab = readTabFromUrl();

  root.innerHTML = `
    <div class="appbar">
      <div class="appbar-title">Friends</div>
    </div>
    <div class="pad" id="friends-page">
      <div class="card">
        <input class="field-input" id="friend-search" autocomplete="off"
               placeholder="Search by username or email" />
        <div id="search-results"></div>
      </div>
      <div class="row" style="gap:6px;overflow-x:auto;flex-wrap:nowrap;margin:6px 0 4px;">
        <button type="button" class="pill tab-btn" data-tab="friends">Friends</button>
        <button type="button" class="pill tab-btn" data-tab="requests">Requests <span class="badge" id="req-badge"></span></button>
        <button type="button" class="pill tab-btn" data-tab="challenges">Challenges <span class="badge" id="chal-badge"></span></button>
      </div>
      <div id="tab-body">
        <div class="skel"></div>
        <div class="skel"></div>
      </div>
    </div>
  `;

  wireSearch();
  wireTabClicks();
  await refreshAll();
}

function wireTabClicks() {
  document.querySelectorAll('.tab-btn').forEach((b) =>
    b.addEventListener('click', async () => {
      currentTab = b.dataset.tab;
      const url = new URL(window.location.href);
      url.searchParams.set('tab', currentTab);
      history.replaceState(null, '', url.pathname + url.search);
      paintTabs();
      await renderTabBody();
    }),
  );
  paintTabs();
}

function paintTabs() {
  document.querySelectorAll('.tab-btn').forEach((b) =>
    b.classList.toggle('on', b.dataset.tab === currentTab),
  );
}

async function refreshAll() {
  // Pre-warm the badges so the user can see request/challenge counts even
  // before they switch tabs.
  try {
    const [reqs, chals] = await Promise.all([
      api('/friends/requests'),
      api('/challenges/incoming'),
    ]);
    setBadge('req-badge', reqs.length);
    setBadge('chal-badge', chals.length);
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      navigate('/login');
      return;
    }
  }
  // The tab-bar badge is global — refresh it so accepting / declining
  // on this page makes the red dot drop right away.
  refreshFriendsBadge();
  await renderTabBody();
}

function setBadge(id, n) {
  const el = document.getElementById(id);
  if (!el) return;
  if (!n) {
    el.textContent = '';
    el.style.display = 'none';
    return;
  }
  el.textContent = String(n);
  el.style.display = '';
  el.style.cssText = 'display:inline-block;background:var(--pink-t);color:#000;border-radius:10px;padding:1px 7px;font-size:10px;font-weight:700;margin-left:4px;';
}

async function renderTabBody() {
  const host = document.getElementById('tab-body');
  if (!host) return;
  host.innerHTML = '<div class="skel"></div><div class="skel"></div>';
  try {
    if (currentTab === 'friends') {
      const [friends, suggestions] = await Promise.all([
        api('/friends'),
        api('/friends/suggestions'),
      ]);
      host.innerHTML = renderFriendsTab(friends, suggestions);
      wireFriendsRowActions();
    } else if (currentTab === 'requests') {
      const [incoming, outgoing] = await Promise.all([
        api('/friends/requests'),
        api('/friends/requests/outgoing'),
      ]);
      host.innerHTML = renderRequestsTab(incoming, outgoing);
      wireRequestRowActions();
    } else {
      const [incoming, outgoing] = await Promise.all([
        api('/challenges/incoming'),
        api('/challenges/outgoing'),
      ]);
      host.innerHTML = renderChallengesTab(incoming, outgoing);
      wireChallengeRowActions();
    }
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      navigate('/login');
      return;
    }
    host.innerHTML = `<div class="card" style="color:var(--pink-t);">${escape(err.message || 'Failed to load.')}</div>`;
  }
}

// ---- search ---------------------------------------------------------------

let _searchTimer = null;
function wireSearch() {
  const input = document.getElementById('friend-search');
  if (!input) return;
  input.addEventListener('input', () => {
    if (_searchTimer) clearTimeout(_searchTimer);
    _searchTimer = setTimeout(() => runSearch(input.value), 250);
  });
}

async function runSearch(q) {
  const host = document.getElementById('search-results');
  if (!host) return;
  const term = (q || '').trim();
  if (term.length < 2) {
    host.innerHTML = '';
    return;
  }
  host.innerHTML = '<div class="muted" style="font-size:12px;margin-top:8px;">Searching…</div>';
  try {
    const results = await api(`/friends/search?q=${encodeURIComponent(term)}`);
    if (!results.length) {
      host.innerHTML = '<div class="muted" style="font-size:12px;margin-top:8px;">No matches.</div>';
      return;
    }
    host.innerHTML = `<div style="margin-top:9px;">${results.map(searchRow).join('')}</div>`;
    host.querySelectorAll('[data-add-id]').forEach((btn) =>
      btn.addEventListener('click', async () => {
        const id = btn.dataset.addId;
        btn.disabled = true;
        try {
          await api('/friends/requests', { method: 'POST', body: { to_user_id: id } });
          toast.success('Request sent');
          btn.textContent = 'Pending';
        } catch (err) {
          toast.error(err.message || 'Could not send request.');
          btn.disabled = false;
        }
      }),
    );
  } catch (err) {
    host.innerHTML = `<div class="muted" style="color:var(--pink-t);font-size:12px;">${escape(err.message)}</div>`;
  }
}

function searchRow(r) {
  const handle = r.user.username || 'Player';
  let cta = '';
  if (r.relationship === 'none') {
    cta = `<button type="button" class="btn teal" data-add-id="${escape(r.user.id)}" style="margin:0;width:auto;padding:6px 12px;">Add</button>`;
  } else if (r.relationship === 'pending_outgoing') {
    cta = '<span class="tag">Pending</span>';
  } else if (r.relationship === 'pending_incoming') {
    cta = '<span class="tag teal">They asked</span>';
  } else if (r.relationship === 'friend') {
    cta = '<span class="tag teal">Friend</span>';
  } else if (r.relationship === 'blocked') {
    cta = '<span class="tag">Blocked</span>';
  } else if (r.relationship === 'self') {
    cta = '<span class="muted" style="font-size:11px;">You</span>';
  }
  const profileHref = `/u/${encodeURIComponent(r.user.id)}`;
  return `
    <div class="row" style="padding:7px 0;border-top:1px solid var(--border);">
      <a href="${profileHref}" data-link class="row grow" style="text-decoration:none;color:inherit;gap:9px;">
        <div class="av sm">${escape(handle.slice(0, 1).toUpperCase())}</div>
        <div class="grow">
          <b style="font-size:13px;color:#fff;">${escape(handle)}</b>
          ${onlineDot(r.user)}
        </div>
      </a>
      ${cta}
    </div>
  `;
}

function onlineDot(u) {
  if (u.is_online) {
    return `<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:var(--teal-t);margin-left:6px;vertical-align:middle;" title="Online"></span>`;
  }
  return '';
}

// ---- friends tab ----------------------------------------------------------

function renderFriendsTab(friends, suggestions) {
  const friendsHtml = friends.length
    ? friends.map(friendRow).join('')
    : `<div class="card center muted" style="padding:18px;">
         <div style="font-family:var(--font-display);font-size:13px;color:#fff;margin-bottom:4px;">No friends yet</div>
         <div style="font-size:12px;">Search by username above or add someone we've suggested below.</div>
       </div>`;
  const sugHtml = suggestions.length
    ? `<h3 class="sec">People you may know</h3>${suggestions.map(suggestionRow).join('')}`
    : '';
  return `
    <h3 class="sec">Your friends · ${friends.length}</h3>
    ${friendsHtml}
    ${sugHtml}
  `;
}

function friendRow(f) {
  const handle = f.user.username || 'Player';
  const profileHref = `/u/${encodeURIComponent(f.user.id)}`;
  return `
    <div class="card pad-sm">
      <div class="row">
        <a href="${profileHref}" data-link class="row grow" style="text-decoration:none;color:inherit;gap:9px;">
          <div class="av sm">${escape(handle.slice(0, 1).toUpperCase())}</div>
          <div class="grow">
            <b style="font-size:13px;color:#fff;">${escape(handle)}</b>
            ${onlineDot(f.user)}
            <div class="muted" style="font-size:11px;">${f.user.is_online ? 'Online now' : 'Offline'}</div>
          </div>
        </a>
        <a href="/messages/new/${encodeURIComponent(f.user.id)}" data-link class="btn ghost" style="margin:0;width:auto;padding:7px 10px;font-size:12px;" title="Message">💬</a>
        <button type="button" class="btn gold" data-challenge-id="${escape(f.user.id)}" data-name="${escape(handle)}" style="margin:0 0 0 6px;width:auto;padding:7px 14px;font-size:12px;">Challenge</button>
        <button type="button" class="btn ghost" data-remove-id="${escape(f.user.id)}" style="margin:0 0 0 6px;width:auto;padding:7px 10px;font-size:12px;" title="Remove">×</button>
      </div>
    </div>
  `;
}

function suggestionRow(s) {
  const handle = s.user.username || 'Player';
  const reasonLabel = {
    mutual_friends: s.mutual_friends > 0 ? `${s.mutual_friends} mutual friend${s.mutual_friends === 1 ? '' : 's'}` : 'Friend of a friend',
    recent_opponent: 'You played them recently',
    referral: 'From your referral',
    popular: 'Popular on Triviana',
  }[s.reason] || '';
  const profileHref = `/u/${encodeURIComponent(s.user.id)}`;
  return `
    <div class="card pad-sm">
      <div class="row">
        <a href="${profileHref}" data-link class="row grow" style="text-decoration:none;color:inherit;gap:9px;">
          <div class="av sm">${escape(handle.slice(0, 1).toUpperCase())}</div>
          <div class="grow">
            <b style="font-size:13px;color:#fff;">${escape(handle)}</b>
            ${onlineDot(s.user)}
            <div class="muted" style="font-size:11px;">${escape(reasonLabel)}</div>
          </div>
        </a>
        <button type="button" class="btn teal" data-add-id="${escape(s.user.id)}" style="margin:0;width:auto;padding:7px 12px;font-size:12px;">Add</button>
        <button type="button" class="btn ghost" data-dismiss-id="${escape(s.user.id)}" style="margin:0 0 0 6px;width:auto;padding:7px 10px;font-size:12px;" title="Hide">×</button>
      </div>
    </div>
  `;
}

function wireFriendsRowActions() {
  document.querySelectorAll('[data-challenge-id]').forEach((btn) =>
    btn.addEventListener('click', () => openChallengeSheet(btn.dataset.challengeId, btn.dataset.name)),
  );
  document.querySelectorAll('[data-remove-id]').forEach((btn) =>
    btn.addEventListener('click', async () => {
      if (!confirm('Remove this friend?')) return;
      try {
        await api(`/friends/${encodeURIComponent(btn.dataset.removeId)}`, { method: 'DELETE' });
        toast.success('Removed');
        renderTabBody();
      } catch (err) {
        toast.error(err.message || 'Could not remove.');
      }
    }),
  );
  document.querySelectorAll('[data-add-id]').forEach((btn) =>
    btn.addEventListener('click', async () => {
      try {
        await api('/friends/requests', { method: 'POST', body: { to_user_id: btn.dataset.addId } });
        toast.success('Request sent');
        renderTabBody();
      } catch (err) {
        toast.error(err.message || 'Could not add.');
      }
    }),
  );
  document.querySelectorAll('[data-dismiss-id]').forEach((btn) =>
    btn.addEventListener('click', async () => {
      try {
        await api(`/friends/suggestions/${encodeURIComponent(btn.dataset.dismissId)}/dismiss`, { method: 'POST' });
        renderTabBody();
      } catch (err) {
        toast.error(err.message);
      }
    }),
  );
}

// ---- requests tab ---------------------------------------------------------

function renderRequestsTab(incoming, outgoing) {
  const inc = incoming.length
    ? incoming.map(requestRow).join('')
    : `<div class="card muted center" style="padding:16px;font-size:12px;">No new requests.</div>`;
  const out = outgoing.length
    ? outgoing.map(requestRow).join('')
    : `<div class="card muted center" style="padding:16px;font-size:12px;">No outgoing requests.</div>`;
  return `
    <h3 class="sec">Incoming</h3>${inc}
    <h3 class="sec">Sent</h3>${out}
  `;
}

function requestRow(r) {
  const handle = r.user.username || 'Player';
  const buttons = r.direction === 'incoming'
    ? `<button type="button" class="btn teal" data-accept="${r.id}" style="margin:0;width:auto;padding:7px 14px;font-size:12px;">Accept</button>
       <button type="button" class="btn ghost" data-decline="${r.id}" style="margin:0 0 0 6px;width:auto;padding:7px 12px;font-size:12px;">Decline</button>`
    : `<button type="button" class="btn ghost" data-cancel="${r.id}" style="margin:0;width:auto;padding:7px 12px;font-size:12px;">Cancel</button>`;
  return `
    <div class="card pad-sm">
      <div class="row">
        <div class="av sm">${escape(handle.slice(0, 1).toUpperCase())}</div>
        <div class="grow">
          <b style="font-size:13px;color:#fff;">${escape(handle)}</b>
          <div class="muted" style="font-size:11px;">${r.direction === 'incoming' ? 'Wants to be your friend' : 'Pending'}</div>
        </div>
        ${buttons}
      </div>
    </div>
  `;
}

function wireRequestRowActions() {
  document.querySelectorAll('[data-accept]').forEach((b) =>
    b.addEventListener('click', () => actOnRequest(b.dataset.accept, 'accept')),
  );
  document.querySelectorAll('[data-decline]').forEach((b) =>
    b.addEventListener('click', () => actOnRequest(b.dataset.decline, 'decline')),
  );
  document.querySelectorAll('[data-cancel]').forEach((b) =>
    b.addEventListener('click', () => actOnRequest(b.dataset.cancel, 'cancel')),
  );
}

async function actOnRequest(id, action) {
  try {
    await api(`/friends/requests/${id}/${action}`, { method: 'POST' });
    toast.success(action === 'accept' ? 'You are now friends' : `Request ${action}d`);
    refreshAll();
  } catch (err) {
    toast.error(err.message || 'Action failed.');
  }
}

// ---- challenges tab -------------------------------------------------------

function renderChallengesTab(incoming, outgoing) {
  const inc = incoming.length
    ? incoming.map(challengeRow).join('')
    : `<div class="card muted center" style="padding:16px;font-size:12px;">No incoming challenges.</div>`;
  const out = outgoing.length
    ? outgoing.map(challengeRow).join('')
    : `<div class="card muted center" style="padding:16px;font-size:12px;">No challenges sent.</div>`;
  return `
    <h3 class="sec">Incoming</h3>${inc}
    <h3 class="sec">Sent</h3>${out}
  `;
}

function challengeRow(c) {
  const other = c.direction === 'incoming' ? c.from_user : c.to_user;
  const handle = other.username || 'Player';
  const stake = c.stake_kobo > 0 ? formatKobo(c.stake_kobo) : 'Free';
  const buttons = c.direction === 'incoming'
    ? `<button type="button" class="btn gold" data-accept-ch="${escape(c.id)}" style="margin:0;width:auto;padding:7px 14px;font-size:12px;">Accept</button>
       <button type="button" class="btn ghost" data-decline-ch="${escape(c.id)}" style="margin:0 0 0 6px;width:auto;padding:7px 12px;font-size:12px;">Decline</button>`
    : `<button type="button" class="btn ghost" data-cancel-ch="${escape(c.id)}" style="margin:0;width:auto;padding:7px 12px;font-size:12px;">Cancel</button>`;
  return `
    <div class="card pad-sm">
      <div class="row">
        <div class="av sm">${escape(handle.slice(0, 1).toUpperCase())}</div>
        <div class="grow">
          <b style="font-size:13px;color:#fff;">${escape(handle)}</b>
          <div class="muted" style="font-size:11px;">
            ${escape(stake)} · ${c.question_count}Q · ${escape(c.difficulty || 'any')}
          </div>
        </div>
        ${buttons}
      </div>
    </div>
  `;
}

function wireChallengeRowActions() {
  document.querySelectorAll('[data-accept-ch]').forEach((b) =>
    b.addEventListener('click', async () => {
      const id = b.dataset.acceptCh;
      try {
        const ch = await api(`/challenges/${encodeURIComponent(id)}/accept`, { method: 'POST' });
        if (ch.match_id) {
          navigate(`/match/${ch.match_id}`);
        } else {
          toast.error('No match id returned — try again.');
        }
      } catch (err) {
        if (err instanceof ApiError && err.code === 'insufficient_balance') {
          toast.error('Not enough balance for that stake.');
        } else {
          toast.error(err.message || 'Could not accept.');
        }
      }
    }),
  );
  document.querySelectorAll('[data-decline-ch]').forEach((b) =>
    b.addEventListener('click', async () => {
      try {
        await api(`/challenges/${encodeURIComponent(b.dataset.declineCh)}/decline`, { method: 'POST' });
        toast.success('Declined');
        refreshAll();
      } catch (err) {
        toast.error(err.message || 'Action failed.');
      }
    }),
  );
  document.querySelectorAll('[data-cancel-ch]').forEach((b) =>
    b.addEventListener('click', async () => {
      try {
        await api(`/challenges/${encodeURIComponent(b.dataset.cancelCh)}/cancel`, { method: 'POST' });
        toast.success('Cancelled');
        refreshAll();
      } catch (err) {
        toast.error(err.message || 'Action failed.');
      }
    }),
  );
}

// ---- challenge bottom sheet ----------------------------------------------

async function openChallengeSheet(friendId, friendName) {
  // Build a quick category/difficulty/stake picker over the screen.
  let categories = [];
  try {
    categories = await api('/game/categories');
  } catch (err) {
    toast.error(err.message || 'No categories.');
    return;
  }
  if (!categories.length) {
    toast.error('No categories available.');
    return;
  }
  let selectedCat = categories[0];
  let selectedDiff = 'medium';
  let selectedStake = 0;

  const overlay = document.createElement('div');
  overlay.className = 'sheet-overlay';
  overlay.innerHTML = `
    <div class="sheet">
      <div class="row" style="margin-bottom:8px;">
        <div class="grow">
          <b style="font-family:var(--font-display);font-size:15px;color:#fff;">Challenge ${escape(friendName)}</b>
          <div class="muted" style="font-size:11px;">10-question duel · winner takes the pool</div>
        </div>
        <button type="button" class="btn ghost" id="sheet-close" style="margin:0;width:auto;padding:7px 10px;font-size:18px;line-height:1;">×</button>
      </div>
      <h3 class="sec" style="margin-top:4px;">Category</h3>
      <div id="ch-cats" class="topic-grid">
        ${categories.map((c) => `<span class="pill" data-cat="${escape(c.id)}">${escape(c.name)}</span>`).join('')}
      </div>
      <h3 class="sec">Difficulty</h3>
      <div id="ch-diffs" class="topic-grid">
        ${['easy', 'medium', 'hard'].map((d) => `<span class="pill" data-diff="${d}">${d}</span>`).join('')}
      </div>
      <h3 class="sec">Stake</h3>
      <div id="ch-stakes" class="topic-grid">
        ${STAKES.map((s) => `<span class="pill" data-stake="${s.kobo}">${s.label}</span>`).join('')}
      </div>
      <button type="button" class="btn gold" id="ch-send" style="margin-top:14px;">Send challenge</button>
    </div>
  `;
  document.body.appendChild(overlay);
  injectSheetStyles();
  const close = () => overlay.remove();
  overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });
  overlay.querySelector('#sheet-close').addEventListener('click', close);

  function refresh() {
    overlay.querySelectorAll('#ch-cats .pill').forEach((p) =>
      p.classList.toggle('on', p.dataset.cat === selectedCat.id),
    );
    overlay.querySelectorAll('#ch-diffs .pill').forEach((p) =>
      p.classList.toggle('on', p.dataset.diff === selectedDiff),
    );
    overlay.querySelectorAll('#ch-stakes .pill').forEach((p) =>
      p.classList.toggle('on', parseInt(p.dataset.stake, 10) === selectedStake),
    );
  }
  refresh();
  overlay.querySelector('#ch-cats').addEventListener('click', (e) => {
    const p = e.target.closest('.pill');
    if (!p) return;
    selectedCat = categories.find((c) => c.id === p.dataset.cat) || selectedCat;
    refresh();
  });
  overlay.querySelector('#ch-diffs').addEventListener('click', (e) => {
    const p = e.target.closest('.pill');
    if (!p) return;
    selectedDiff = p.dataset.diff;
    refresh();
  });
  overlay.querySelector('#ch-stakes').addEventListener('click', (e) => {
    const p = e.target.closest('.pill');
    if (!p) return;
    selectedStake = parseInt(p.dataset.stake, 10) || 0;
    refresh();
  });

  overlay.querySelector('#ch-send').addEventListener('click', async () => {
    const btn = overlay.querySelector('#ch-send');
    btn.disabled = true;
    btn.textContent = 'Sending…';
    try {
      await api('/challenges', {
        method: 'POST',
        body: {
          to_user_id: friendId,
          category_id: selectedCat.id,
          difficulty: selectedDiff,
          stake_kobo: selectedStake,
          question_count: 10,
        },
      });
      toast.success('Challenge sent');
      close();
      currentTab = 'challenges';
      const url = new URL(window.location.href);
      url.searchParams.set('tab', 'challenges');
      history.replaceState(null, '', url.pathname + url.search);
      paintTabs();
      renderTabBody();
    } catch (err) {
      btn.disabled = false;
      btn.textContent = 'Send challenge';
      if (err instanceof ApiError && err.code === 'insufficient_balance') {
        toast.error('Not enough balance for that stake.');
      } else {
        toast.error(err.message || 'Could not send.');
      }
    }
  });
}

function injectSheetStyles() {
  if (document.getElementById('friends-sheet-styles')) return;
  const s = document.createElement('style');
  s.id = 'friends-sheet-styles';
  s.textContent = `
    .sheet-overlay { position:fixed; inset:0; background:rgba(0,0,0,0.5); z-index:60; display:flex; align-items:flex-end; justify-content:center; }
    .sheet { background:var(--surf-2); border-top:1px solid var(--violet); border-radius:14px 14px 0 0; padding:18px; width:100%; max-width:480px; max-height:80vh; overflow:auto; }
  `;
  document.head.appendChild(s);
}
